// src/admin/components/BulkProductUpload.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Stack,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Divider,
    Tooltip,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Badge,
} from '@mui/material';
import {
    Upload as UploadIcon,
    Download as DownloadIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
    Category,
    VariantType,
    Warehouse,
    ProductFormData,
    ProductVariant,
    VariantSelection,
    WarehouseAllocation,
} from '../../../enums/product';
import { ProductService } from '../product/productService';
import { CategoryService } from '../category/categoryService';
import { VariantService } from '../variant/variantService';
import { WarehouseService } from '../warehouse/warehouseService';
import { Brand, fetchActiveBrands } from '../brand/brandService';
import { useAuth } from '../../../contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

interface ParsedRow {
    rowIndex: number;     // 1-based (excluding header)
    raw: Record<string, string>;
}

type RowStatus = 'pending' | 'uploading' | 'success' | 'error' | 'skipped';

interface ProcessedProduct {
    rowIndex: number;
    productName: string;
    sku: string;
    status: RowStatus;
    error?: string;
    warnings?: string[];
    formData?: ProductFormData;
}

interface UploadStats {
    total: number;
    success: number;
    failed: number;
    skipped: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV COLUMN DEFINITIONS
// These are the exact header names expected in the CSV.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * COLUMN REFERENCE
 *
 * REQUIRED:
 *   product_name          - Product display name
 *   has_variants          - "yes" / "no"
 *   category_name         - Must match an existing category name (case-insensitive)
 *
 * SIMPLE PRODUCTS (has_variants = no):
 *   price                 - Numeric price
 *   sku                   - Unique SKU
 *   discount              - Discount % (0-100), default 0
 *   stock_quantity        - Total stock (split evenly if multiple warehouses)
 *   min_stock_level       - Default 10
 *   max_stock_level       - Default 100
 *
 * VARIANT PRODUCTS (has_variants = yes):
 *   variant_type_1        - e.g. "Size"
 *   variant_value_1       - e.g. "M"
 *   variant_type_2        - e.g. "Color" (optional second dimension)
 *   variant_value_2       - e.g. "Red"  (optional second dimension)
 *   variant_price         - Price for this variant
 *   variant_sku           - SKU for this variant
 *   variant_discount      - Discount % for this variant (0-100), default 0
 *   variant_stock         - Stock for this variant
 *   variant_min_stock     - Default 10
 *   variant_max_stock     - Default 100
 *   variant_is_default    - "yes" / "no" — marks which variant is default
 *
 * OPTIONAL (all products):
 *   description           - Plain text description (HTML supported)
 *   subcategory_name      - Must be a child of category_name (case-insensitive)
 *   brand_name            - Must match an existing brand name (case-insensitive)
 *   is_out_of_stock       - "yes" / "no" (override), default "no"
 *   is_featured           - "yes" / "no", default "no"
 *
 * WAREHOUSE ALLOCATION (optional — if omitted, stock goes to first active warehouse):
 *   warehouse_1_name      - Warehouse name (case-insensitive)
 *   warehouse_1_qty       - Quantity for warehouse_1
 *   warehouse_1_min       - Min stock level for warehouse_1
 *   warehouse_1_max       - Max stock level for warehouse_1
 *   warehouse_2_name      - (optional second warehouse)
 *   warehouse_2_qty
 *   warehouse_2_min
 *   warehouse_2_max
 *   warehouse_3_name      - (optional third warehouse)
 *   warehouse_3_qty
 *   warehouse_3_min
 *   warehouse_3_max
 *
 * MULTI-ROW VARIANTS:
 *   Multiple rows with the same product_name describe variants of one product.
 *   All non-variant columns (description, category, etc.) are taken from the
 *   FIRST row of each product group. Subsequent rows only need product_name +
 *   variant_* columns.
 */

const MAX_WAREHOUSES = 3;
const MAX_VARIANT_TYPES = 2;

// ─────────────────────────────────────────────────────────────────────────────
// SAMPLE CSV GENERATOR
// ─────────────────────────────────────────────────────────────────────────────

function generateSampleCSV(
    categories: Category[],
    brands: Brand[],
    warehouses: Warehouse[],
    variantTypes: VariantType[]
): string {
    const catName = categories[0]?.name || 'Electronics';
    const subCatName = '';
    const brandName = brands[0]?.name || '';
    const wh1 = warehouses[0]?.name || 'Main Warehouse';
    const wh2 = warehouses[1]?.name || '';
    const vt1 = variantTypes[0]?.name || 'Size';
    const vt1Opt1 = variantTypes[0]?.options?.[0]?.name || 'S';
    const vt1Opt2 = variantTypes[0]?.options?.[1]?.name || 'M';
    const vt2 = variantTypes[1]?.name || 'Color';
    const vt2Opt1 = variantTypes[1]?.options?.[0]?.name || 'Red';

    const headers = [
        'product_name',
        'has_variants',
        'description',
        'category_name',
        'subcategory_name',
        'brand_name',
        'is_out_of_stock',
        'is_featured',
        // Simple product fields
        'price',
        'discount',
        'sku',
        'stock_quantity',
        'min_stock_level',
        'max_stock_level',
        // Variant fields
        'variant_type_1',
        'variant_value_1',
        'variant_type_2',
        'variant_value_2',
        'variant_price',
        'variant_sku',
        'variant_discount',
        'variant_stock',
        'variant_min_stock',
        'variant_max_stock',
        'variant_is_default',
        // Warehouse allocation
        'warehouse_1_name',
        'warehouse_1_qty',
        'warehouse_1_min',
        'warehouse_1_max',
        'warehouse_2_name',
        'warehouse_2_qty',
        'warehouse_2_min',
        'warehouse_2_max',
        'warehouse_3_name',
        'warehouse_3_qty',
        'warehouse_3_min',
        'warehouse_3_max',
    ];

    // Row 1: Simple product with warehouse allocation
    const row1 = [
        'Wireless Mouse', 'no', 'A high-precision wireless mouse with ergonomic design.',
        catName, subCatName, brandName, 'no', 'no',
        '29.99', '10', 'WM-001', '150', '10', '200',
        '', '', '', '', '', '', '', '', '', '', '',
        wh1, '100', '10', '200',
        wh2, wh2 ? '50' : '', wh2 ? '5' : '', wh2 ? '100' : '',
        '', '', '', '',
    ];

    // Row 2: Simple product, no discount, no second warehouse
    const row2 = [
        'USB-C Hub', 'no', 'Multi-port USB-C hub with 4K HDMI support.',
        catName, '', brandName, 'no', 'yes',
        '49.99', '0', 'USBHUB-01', '80', '5', '150',
        '', '', '', '', '', '', '', '', '', '', '',
        wh1, '80', '5', '150',
        '', '', '', '',
        '', '', '', '',
    ];

    // Row 3: Variant product — first variant (is_default = yes)
    const row3 = [
        'Classic T-Shirt', 'yes', '<p>Premium cotton t-shirt available in multiple sizes and colors.</p>',
        catName, subCatName, brandName, 'no', 'no',
        '', '', '', '', '', '',
        vt1, vt1Opt1, vt2, vt2Opt1, '19.99', `TS-${vt1Opt1}-${vt2Opt1}`, '0', '50', '5', '100', 'yes',
        wh1, '50', '5', '100',
        '', '', '', '',
        '', '', '', '',
    ];

    // Row 4: Variant product — second variant (same product_name)
    const row4 = [
        'Classic T-Shirt', 'yes', '',
        catName, '', '', '', '',
        '', '', '', '', '', '',
        vt1, vt1Opt2, vt2, vt2Opt1, '19.99', `TS-${vt1Opt2}-${vt2Opt1}`, '5', '40', '5', '100', 'no',
        wh1, '40', '5', '100',
        '', '', '', '',
        '', '', '', '',
    ];

    const escape = (v: string) => {
        if (v.includes(',') || v.includes('"') || v.includes('\n')) {
            return `"${v.replace(/"/g, '""')}"`;
        }
        return v;
    };

    const rows = [headers, row1, row2, row3, row4]
        .map(r => r.map(escape).join(','))
        .join('\n');

    return rows;
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV PARSER  (no external dependency)
// ─────────────────────────────────────────────────────────────────────────────

function parseCSV(text: string): { headers: string[]; rows: Record<string, string>[] } {
    const lines: string[] = [];
    let cur = '';
    let inQuote = false;

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === '"') {
            if (inQuote && text[i + 1] === '"') { cur += '"'; i++; }
            else { inQuote = !inQuote; }
        } else if (ch === '\n' && !inQuote) {
            lines.push(cur.replace(/\r$/, ''));
            cur = '';
        } else {
            cur += ch;
        }
    }
    if (cur.trim()) lines.push(cur.replace(/\r$/, ''));

    if (lines.length < 2) return { headers: [], rows: [] };

    const splitLine = (line: string): string[] => {
        const cols: string[] = [];
        let col = '';
        let q = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
                if (q && line[i + 1] === '"') { col += '"'; i++; }
                else { q = !q; }
            } else if (ch === ',' && !q) {
                cols.push(col.trim());
                col = '';
            } else {
                col += ch;
            }
        }
        cols.push(col.trim());
        return cols;
    };

    const headers = splitLine(lines[0]).map(h => h.toLowerCase().trim());
    const rows: Record<string, string>[] = [];

    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        const cols = splitLine(line);
        const row: Record<string, string> = {};
        headers.forEach((h, idx) => { row[h] = (cols[idx] ?? '').trim(); });
        rows.push(row);
    }

    return { headers, rows };
}

// ─────────────────────────────────────────────────────────────────────────────
// ROW → ProductFormData TRANSFORMER
// ─────────────────────────────────────────────────────────────────────────────

interface TransformContext {
    categories: Category[];
    brands: Brand[];
    warehouses: Warehouse[];
    variantTypes: VariantType[];
    subcategoryMap: Map<string, Category[]>; // parentId → subcategories
}

function bool(v: string): boolean {
    return v.toLowerCase() === 'yes' || v === '1' || v.toLowerCase() === 'true';
}

function resolveCategory(
    name: string,
    ctx: TransformContext
): { category: Category | null; error?: string } {
    if (!name.trim()) return { category: null, error: 'category_name is required' };
    const found = ctx.categories.find(c => c.name.toLowerCase() === name.toLowerCase());
    if (!found) return { category: null, error: `Category "${name}" not found` };
    return { category: found };
}

function resolveSubcategory(
    name: string,
    parentId: string,
    ctx: TransformContext
): { subcategory: Category | null; error?: string } {
    if (!name.trim()) return { subcategory: null };
    const subs = ctx.subcategoryMap.get(parentId) || [];
    const found = subs.find(s => s.name.toLowerCase() === name.toLowerCase());
    if (!found) return { subcategory: null, error: `Subcategory "${name}" not found under selected category` };
    return { subcategory: found };
}

function resolveBrand(
    name: string,
    ctx: TransformContext
): { brand: Brand | null; error?: string } {
    if (!name.trim()) return { brand: null };
    const found = ctx.brands.find(b => b.name.toLowerCase() === name.toLowerCase());
    if (!found) return { brand: null, error: `Brand "${name}" not found` };
    return { brand: found };
}

function resolveWarehouse(
    name: string,
    ctx: TransformContext
): { warehouse: Warehouse | null; error?: string } {
    if (!name.trim()) return { warehouse: null };
    const found = ctx.warehouses.find(w => w.isActive && w.name.toLowerCase() === name.toLowerCase());
    if (!found) return { warehouse: null, error: `Active warehouse "${name}" not found` };
    return { warehouse: found };
}

/**
 * Build warehouse allocations from a row.
 * If no warehouse columns supplied, falls back to first active warehouse with full stock.
 */
function buildWarehouseAllocations(
    row: Record<string, string>,
    totalStock: number,
    minStock: number,
    maxStock: number,
    ctx: TransformContext
): { allocations: WarehouseAllocation[]; errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];
    const allocations: WarehouseAllocation[] = [];

    let hasExplicitWarehouses = false;

    for (let i = 1; i <= MAX_WAREHOUSES; i++) {
        const wName = row[`warehouse_${i}_name`] || '';
        if (!wName) continue;
        hasExplicitWarehouses = true;

        const { warehouse, error } = resolveWarehouse(wName, ctx);
        if (error || !warehouse) { errors.push(error || `warehouse_${i}_name invalid`); continue; }

        const qty = parseInt(row[`warehouse_${i}_qty`] || '0', 10) || 0;
        const min = parseInt(row[`warehouse_${i}_min`] || String(minStock), 10) || minStock;
        const max = parseInt(row[`warehouse_${i}_max`] || String(maxStock), 10) || maxStock;

        allocations.push({
            warehouseId: warehouse.id!,
            warehouseName: warehouse.name,
            quantity: qty,
            minStockLevel: min,
            maxStockLevel: max,
            isLowStock: qty > 0 && qty <= min,
            isOutOfStock: qty === 0,
            isOverStock: qty > 0 && qty >= max,
        });
    }

    // Fallback: assign to first active warehouse
    if (!hasExplicitWarehouses) {
        const firstWH = ctx.warehouses.find(w => w.isActive);
        if (firstWH) {
            warnings.push(`No warehouse columns found — assigned all stock to "${firstWH.name}"`);
            allocations.push({
                warehouseId: firstWH.id!,
                warehouseName: firstWH.name,
                quantity: totalStock,
                minStockLevel: minStock,
                maxStockLevel: maxStock,
                isLowStock: totalStock > 0 && totalStock <= minStock,
                isOutOfStock: totalStock === 0,
                isOverStock: totalStock > 0 && totalStock >= maxStock,
            });
        } else {
            errors.push('No active warehouses available');
        }
    }

    return { allocations, errors, warnings };
}

// ─────────────────────────────────────────────────────────────────────────────
// GROUP ROWS BY product_name  (preserving order)
// ─────────────────────────────────────────────────────────────────────────────

interface ProductGroup {
    productName: string;
    rows: Record<string, string>[];
    firstRowIndex: number; // 1-based
}

function groupRows(parsedRows: ParsedRow[]): ProductGroup[] {
    const map = new Map<string, ProductGroup>();
    const order: string[] = [];

    for (const { rowIndex, raw } of parsedRows) {
        const name = (raw['product_name'] || '').trim();
        if (!name) continue;
        if (!map.has(name)) {
            map.set(name, { productName: name, rows: [], firstRowIndex: rowIndex });
            order.push(name);
        }
        map.get(name)!.rows.push(raw);
    }

    return order.map(n => map.get(n)!);
}

// ─────────────────────────────────────────────────────────────────────────────
// TRANSFORM: one ProductGroup → ProcessedProduct
// ─────────────────────────────────────────────────────────────────────────────

function transformGroup(
    group: ProductGroup,
    ctx: TransformContext,
    variantIdCounter: { v: number }
): ProcessedProduct {
    const errors: string[] = [];
    const warnings: string[] = [];
    const first = group.rows[0];

    // ── Core fields ──
    const productName = group.productName;
    const hasVariants = bool(first['has_variants'] || 'no');
    const description = first['description'] || '';
    const isOutOfStock = bool(first['is_out_of_stock'] || 'no');
    const isFeatured = bool(first['is_featured'] || 'no');

    // ── Category ──
    const { category, error: catError } = resolveCategory(first['category_name'] || '', ctx);
    if (catError) errors.push(catError);

    // ── Subcategory ──
    let subcategoryId = '';
    if (category) {
        const { subcategory, error: subError } = resolveSubcategory(
            first['subcategory_name'] || '',
            category.id!,
            ctx
        );
        if (subError) warnings.push(subError);
        if (subcategory) subcategoryId = subcategory.id!;
    }

    // ── Brand ──
    const { brand, error: brandError } = resolveBrand(first['brand_name'] || '', ctx);
    if (brandError) warnings.push(brandError);

    // ── minStockLevel / maxStockLevel (product-level) ──
    const minStockLevel = first['min_stock_level'] || (hasVariants ? '' : '10');
    const maxStockLevel = first['max_stock_level'] || (hasVariants ? '' : '100');

    // ── SIMPLE PRODUCT ──────────────────────────────────────────────────────────
    if (!hasVariants) {
        const price = first['price'] || '';
        const sku = first['sku'] || '';
        const discount = first['discount'] || '0';
        const unitCost = first['unitCost'] || '0';
        const stockQty = parseInt(first['stock_quantity'] || '0', 10) || 0;
        const minSL = parseInt(minStockLevel, 10) || 10;
        const maxSL = parseInt(maxStockLevel, 10) || 100;

        if (!price || parseFloat(price) <= 0) errors.push('price must be a positive number');
        if (!sku.trim()) errors.push('sku is required for simple products');
        const disc = parseFloat(discount) || 0;
        if (disc < 0 || disc > 100) errors.push('discount must be between 0 and 100');

        const { allocations, errors: whErrors, warnings: whWarnings } = buildWarehouseAllocations(
            first, stockQty, minSL, maxSL, ctx
        );
        errors.push(...whErrors);
        warnings.push(...whWarnings);

        // Validate allocation total matches stockQty
        const allocTotal = allocations.reduce((s, a) => s + a.quantity, 0);
        if (allocations.length > 0 && allocTotal !== stockQty) {
            warnings.push(
                `Warehouse quantity total (${allocTotal}) differs from stock_quantity (${stockQty}). ` +
                `Using warehouse quantities.`
            );
        }

        const formData: ProductFormData = {
            name: productName,
            description,
            price,
            discount,
            unitCost,
            isOutOfStock,
            isFeatured,
            categoryId: category?.id || '',
            subcategoryId,
            brandId: brand?.id || '',
            sku,
            images: [],
            variants: [],
            hasVariants: false,
            stockQuantity: String(stockQty),
            minStockLevel,
            maxStockLevel,
            warehouseAllocations: allocations,
        };

        return {
            rowIndex: group.firstRowIndex,
            productName,
            sku,
            status: errors.length > 0 ? 'error' : 'pending',
            error: errors.join('; '),
            warnings,
            formData: errors.length === 0 ? formData : undefined,
        };
    }

    // ── VARIANT PRODUCT ─────────────────────────────────────────────────────────
    const variants: ProductVariant[] = [];
    let hasDefaultVariant = false;

    for (let ri = 0; ri < group.rows.length; ri++) {
        const row = group.rows[ri];
        const rowErrors: string[] = [];

        // Variant selections
        const selections: VariantSelection[] = [];
        for (let d = 1; d <= MAX_VARIANT_TYPES; d++) {
            const vType = (row[`variant_type_${d}`] || '').trim();
            const vVal = (row[`variant_value_${d}`] || '').trim();
            if (vType && vVal) {
                // Check variant type exists
                const vtExists = ctx.variantTypes.some(vt => vt.name.toLowerCase() === vType.toLowerCase());
                if (!vtExists) {
                    rowErrors.push(`variant_type_${d} "${vType}" not found`);
                }
                selections.push({ variantType: vType, variantValue: vVal });
            }
        }
        if (selections.length === 0) {
            rowErrors.push('At least one variant_type/variant_value pair is required');
        }

        const vPrice = parseFloat(row['variant_price'] || '0') || 0;
        const vSku = (row['variant_sku'] || '').trim();
        const vDiscount = parseFloat(row['variant_discount'] || '0') || 0;
        const vStock = parseInt(row['variant_stock'] || '0', 10) || 0;
        const vMin = parseInt(row['variant_min_stock'] || '10', 10) || 10;
        const vMax = parseInt(row['variant_max_stock'] || '100', 10) || 100;
        const vIsDefault = bool(row['variant_is_default'] || 'no');

        if (vPrice <= 0) rowErrors.push(`Row ${group.firstRowIndex + ri}: variant_price must be positive`);
        if (!vSku) rowErrors.push(`Row ${group.firstRowIndex + ri}: variant_sku is required`);
        if (vDiscount < 0 || vDiscount > 100) rowErrors.push(`Row ${group.firstRowIndex + ri}: variant_discount out of range`);

        const { allocations: vAllocs, errors: vWhErrors, warnings: vWhWarnings } = buildWarehouseAllocations(
            row, vStock, vMin, vMax, ctx
        );
        rowErrors.push(...vWhErrors);
        warnings.push(...vWhWarnings);

        if (rowErrors.length > 0) {
            errors.push(...rowErrors);
            continue;
        }

        if (vIsDefault) hasDefaultVariant = true;

        const variantId = `var-${++variantIdCounter.v}`;
        variants.push({
            id: variantId,
            variantSelections: selections,
            price: vPrice,
            discount: vDiscount,
            sku: vSku,
            isDefault: vIsDefault,
            stockQuantity: vStock,
            minStockLevel: vMin,
            maxStockLevel: vMax,
            isLowStock: vStock > 0 && vStock <= vMin,
            isOutOfStock: vStock === 0,
            isOverStock: vStock > 0 && vStock >= vMax,
            warehouseAllocations: vAllocs,
        });
    }

    if (variants.length === 0) {
        errors.push('No valid variants found for this product');
    }

    // Ensure at least one variant is default
    if (variants.length > 0 && !hasDefaultVariant) {
        variants[0] = { ...variants[0], isDefault: true };
        warnings.push('No variant_is_default=yes found — first variant set as default');
    }

    // Check for duplicate variant combinations
    const seen = new Set<string>();
    for (const v of variants) {
        const key = v.variantSelections.map(s => `${s.variantType}:${s.variantValue}`).sort().join('|');
        if (seen.has(key)) errors.push(`Duplicate variant combination: ${key}`);
        seen.add(key);
    }

    const mainSku = variants[0]?.sku || '';

    const formData: ProductFormData = {
        name: productName,
        description,
        price: '0',
        discount: '0',
        unitCost: '0',
        isOutOfStock,
        isFeatured,
        categoryId: category?.id || '',
        subcategoryId,
        brandId: brand?.id || '',
        sku: '',
        images: [],
        variants,
        hasVariants: true,
        stockQuantity: String(variants.reduce((s, v) => s + v.stockQuantity, 0)),
        minStockLevel: minStockLevel || '10',
        maxStockLevel: maxStockLevel || '100',
        warehouseAllocations: [],
    };

    return {
        rowIndex: group.firstRowIndex,
        productName,
        sku: mainSku,
        status: errors.length > 0 ? 'error' : 'pending',
        error: errors.join('; '),
        warnings,
        formData: errors.length === 0 ? formData : undefined,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// BULK UPLOAD COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

interface BulkProductUploadProps {
    open: boolean;
    onClose: () => void;
    onComplete: () => void;
    onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const BulkProductUpload: React.FC<BulkProductUploadProps> = ({
    open,
    onClose,
    onComplete,
    onShowToast,
}) => {
    const { currentUser } = useAuth();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Reference data
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [variantTypes, setVariantTypes] = useState<VariantType[]>([]);
    const [subcategoryMap, setSubcategoryMap] = useState<Map<string, Category[]>>(new Map());
    const [loadingRef, setLoadingRef] = useState(false);

    // Upload state
    const [processedProducts, setProcessedProducts] = useState<ProcessedProduct[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [stats, setStats] = useState<UploadStats>({ total: 0, success: 0, failed: 0, skipped: 0 });
    const [phase, setPhase] = useState<'idle' | 'preview' | 'uploading' | 'done'>('idle');
    const [fileError, setFileError] = useState('');

    // ── Load reference data when dialog opens ──
    useEffect(() => {
        if (open) loadReferenceData();
    }, [open]);

    const loadReferenceData = async () => {
        try {
            setLoadingRef(true);
            const [cats, vts, whs, brs] = await Promise.all([
                CategoryService.getMainCategories(),
                VariantService.getActiveVariantTypes(),
                WarehouseService.getActiveWarehouses(),
                fetchActiveBrands(),
            ]);
            setCategories(cats);
            setVariantTypes(vts);
            setWarehouses(whs);
            setBrands(brs);

            // Build subcategory map
            const subMap = new Map<string, Category[]>();
            await Promise.all(
                cats.map(async cat => {
                    try {
                        const subs = await CategoryService.getSubcategories(cat.id!);
                        subMap.set(cat.id!, subs);
                    } catch { /* ignore */ }
                })
            );
            setSubcategoryMap(subMap);
        } catch (e) {
            console.error('Error loading reference data:', e);
            onShowToast('Failed to load reference data', 'error');
        } finally {
            setLoadingRef(false);
        }
    };

    // ── Download sample CSV ──
    const handleDownloadSample = () => {
        const csv = generateSampleCSV(categories, brands, warehouses, variantTypes);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sample_products.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    // ── File selection ──
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.endsWith('.csv')) {
            setFileError('Please upload a .csv file');
            return;
        }
        setFileError('');
        const reader = new FileReader();
        reader.onload = ev => {
            const text = ev.target?.result as string;
            processCSV(text);
        };
        reader.readAsText(file);
        // Reset input so the same file can be re-selected
        e.target.value = '';
    };

    const processCSV = (text: string) => {
        setPhase('idle');
        const { headers, rows } = parseCSV(text);

        if (headers.length === 0 || rows.length === 0) {
            setFileError('CSV file is empty or has no data rows');
            return;
        }

        // Validate required headers
        const required = ['product_name', 'has_variants', 'category_name'];
        const missing = required.filter(h => !headers.includes(h));
        if (missing.length > 0) {
            setFileError(`Missing required columns: ${missing.join(', ')}`);
            return;
        }

        const parsedRows: ParsedRow[] = rows.map((raw, idx) => ({ rowIndex: idx + 1, raw }));
        const groups = groupRows(parsedRows);

        const ctx: TransformContext = {
            categories,
            brands,
            warehouses: warehouses.filter(w => w.isActive),
            variantTypes,
            subcategoryMap,
        };

        const variantIdCounter = { v: 0 };
        const processed = groups.map(g => transformGroup(g, ctx, variantIdCounter));

        setProcessedProducts(processed);
        const total = processed.length;
        const failed = processed.filter(p => p.status === 'error').length;
        setStats({ total, success: 0, failed, skipped: 0 });
        setPhase('preview');
    };

    // ── Upload all valid products ──
    const handleUpload = async () => {
        const toUpload = processedProducts.filter(p => p.status === 'pending' && p.formData);
        if (toUpload.length === 0) {
            onShowToast('No valid products to upload', 'warning');
            return;
        }

        setUploading(true);
        setPhase('uploading');
        let success = 0;
        let failed = processedProducts.filter(p => p.status === 'error').length;
        let skipped = 0;

        const update = (idx: number, status: RowStatus, error?: string) => {
            setProcessedProducts(prev =>
                prev.map((p, i) => i === idx ? { ...p, status, error } : p)
            );
        };

        for (let i = 0; i < processedProducts.length; i++) {
            const pp = processedProducts[i];
            if (pp.status !== 'pending' || !pp.formData) {
                if (pp.status === 'error') { /* already counted */ }
                else { skipped++; }
                setUploadProgress(Math.round(((i + 1) / processedProducts.length) * 100));
                continue;
            }

            update(i, 'uploading');
            try {
                await ProductService.createProduct(pp.formData, currentUser?.uid || '');
                update(i, 'success');
                success++;
            } catch (e: any) {
                update(i, 'error', e.message || 'Unknown error');
                failed++;
            }
            setUploadProgress(Math.round(((i + 1) / processedProducts.length) * 100));
        }

        setStats({ total: processedProducts.length, success, failed, skipped });
        setUploading(false);
        setPhase('done');

        if (success > 0) {
            onShowToast(`${success} product${success !== 1 ? 's' : ''} uploaded successfully`, 'success');
            onComplete();
        }
        if (failed > 0) {
            onShowToast(`${failed} product${failed !== 1 ? 's' : ''} failed`, 'error');
        }
    };

    const handleReset = () => {
        setProcessedProducts([]);
        setStats({ total: 0, success: 0, failed: 0, skipped: 0 });
        setPhase('idle');
        setUploadProgress(0);
        setFileError('');
    };

    const handleClose = () => {
        if (uploading) return;
        handleReset();
        onClose();
    };

    const pendingCount = processedProducts.filter(p => p.status === 'pending').length;
    const errorCount = processedProducts.filter(p => p.status === 'error').length;
    const successCount = processedProducts.filter(p => p.status === 'success').length;

    const statusColor = (s: RowStatus) => {
        switch (s) {
            case 'success': return 'success';
            case 'error': return 'error';
            case 'uploading': return 'info';
            case 'skipped': return 'default';
            default: return 'default';
        }
    };

    const statusLabel = (s: RowStatus) => {
        switch (s) {
            case 'success': return 'Uploaded';
            case 'error': return 'Error';
            case 'uploading': return 'Uploading…';
            case 'skipped': return 'Skipped';
            default: return 'Ready';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
        >
            {/* ── Header ── */}
            <DialogTitle sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <UploadIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Bulk Product Upload</Typography>
                        {phase === 'preview' && (
                            <Chip
                                label={`${processedProducts.length} products parsed`}
                                size="small" color="primary" variant="outlined"
                            />
                        )}
                    </Stack>
                    <IconButton onClick={handleClose} size="small" disabled={uploading}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            {/* ── Content ── */}
            <DialogContent sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                {loadingRef ? (
                    <Box display="flex" alignItems="center" justifyContent="center" py={8}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            Loading reference data…
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* ── STEP 1: Instructions + download sample ── */}
                        <Accordion defaultExpanded={phase === 'idle'} sx={{ mb: 2, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important' }} elevation={0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <InfoIcon color="primary" sx={{ fontSize: 18 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>CSV Format Guide & Sample Download</Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Required Columns</Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {['product_name', 'has_variants', 'category_name'].map(c => (
                                                <Chip key={c} label={c} size="small" color="error" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                                            ))}
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Simple Products (has_variants = no)</Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {['price', 'sku', 'discount', 'stock_quantity', 'min_stock_level', 'max_stock_level'].map(c => (
                                                <Chip key={c} label={c} size="small" color="primary" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                                            ))}
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Variant Products (has_variants = yes) — one row per variant</Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {[
                                                'variant_type_1', 'variant_value_1',
                                                'variant_type_2 (opt)', 'variant_value_2 (opt)',
                                                'variant_price', 'variant_sku', 'variant_discount',
                                                'variant_stock', 'variant_min_stock', 'variant_max_stock',
                                                'variant_is_default',
                                            ].map(c => (
                                                <Chip key={c} label={c} size="small" color="secondary" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                                            ))}
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Warehouse Allocation (up to 3 warehouses)</Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {[
                                                'warehouse_1_name', 'warehouse_1_qty', 'warehouse_1_min', 'warehouse_1_max',
                                                'warehouse_2_name', 'warehouse_2_qty', 'warehouse_2_min', 'warehouse_2_max',
                                                'warehouse_3_name', 'warehouse_3_qty', 'warehouse_3_min', 'warehouse_3_max',
                                            ].map(c => (
                                                <Chip key={c} label={c} size="small" color="info" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                                            ))}
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Optional Columns</Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {['description', 'subcategory_name', 'brand_name', 'is_out_of_stock', 'is_featured'].map(c => (
                                                <Chip key={c} label={c} size="small" color="default" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                                            ))}
                                        </Box>
                                    </Box>

                                    <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                                        <Typography variant="body2">
                                            <strong>Multi-row variants:</strong> Group variants for the same product by using the same{' '}
                                            <code>product_name</code>. All shared fields (category, description, brand, etc.) are taken
                                            from the <em>first</em> row of each group.
                                        </Typography>
                                    </Alert>

                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                            Your available reference data
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            <Chip label={`${categories.length} categories`} size="small" color="primary" variant="outlined" />
                                            <Chip label={`${brands.length} brands`} size="small" color="secondary" variant="outlined" />
                                            <Chip label={`${warehouses.filter(w => w.isActive).length} active warehouses`} size="small" color="info" variant="outlined" />
                                            <Chip label={`${variantTypes.length} variant types`} size="small" color="success" variant="outlined" />
                                        </Stack>
                                    </Box>

                                    <Button
                                        startIcon={<DownloadIcon />}
                                        variant="outlined"
                                        size="small"
                                        onClick={handleDownloadSample}
                                        sx={{ alignSelf: 'flex-start' }}
                                    >
                                        Download Sample CSV
                                    </Button>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>

                        {/* ── STEP 2: File Upload ── */}
                        {phase === 'idle' && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    borderStyle: 'dashed',
                                    borderColor: 'primary.light',
                                    bgcolor: 'primary.50',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.100' },
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <UploadIcon sx={{ fontSize: 48, color: 'primary.light', mb: 1 }} />
                                <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Click to select your CSV file
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Only .csv files are supported
                                </Typography>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    hidden
                                    onChange={handleFileChange}
                                />
                            </Paper>
                        )}

                        {fileError && (
                            <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>{fileError}</Alert>
                        )}

                        {/* ── STEP 3: Preview Table ── */}
                        {(phase === 'preview' || phase === 'uploading' || phase === 'done') && processedProducts.length > 0 && (
                            <>
                                {/* Stats bar */}
                                <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
                                    <Chip
                                        label={`${processedProducts.length} total`}
                                        size="small" color="default" variant="outlined"
                                    />
                                    <Chip
                                        icon={<CheckCircleIcon />}
                                        label={`${pendingCount + successCount} ready / uploaded`}
                                        size="small" color="success" variant="outlined"
                                    />
                                    <Chip
                                        icon={<ErrorIcon />}
                                        label={`${errorCount} errors`}
                                        size="small" color="error" variant="outlined"
                                    />
                                    <Chip
                                        label={`${uploadProgress}% complete`}
                                        size="small" color="info" variant="outlined"
                                    />
                                </Stack>

                                {phase === 'uploading' && (
                                    <Box sx={{ mb: 2 }}>
                                        <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 3 }} />
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            Uploading… {uploadProgress}%
                                        </Typography>
                                    </Box>
                                )}

                                {phase === 'done' && (
                                    <Alert
                                        severity={stats.failed > 0 && stats.success === 0 ? 'error' : stats.failed > 0 ? 'warning' : 'success'}
                                        sx={{ mb: 2, borderRadius: 1.5 }}
                                    >
                                        <Typography variant="body2">
                                            Upload complete — <strong>{stats.success} succeeded</strong>, {stats.failed} failed, {stats.skipped} skipped.
                                        </Typography>
                                    </Alert>
                                )}

                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                <TableCell width={60} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Row</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Product Name</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>SKU</TableCell>
                                                <TableCell width={110} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Type</TableCell>
                                                <TableCell width={110} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Details</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {processedProducts.map((pp, idx) => (
                                                <TableRow
                                                    key={idx}
                                                    sx={{
                                                        bgcolor: pp.status === 'error' ? 'error.50' : pp.status === 'success' ? 'success.50' : 'inherit',
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                            #{pp.rowIndex}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {pp.productName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                            {pp.sku || '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={pp.formData?.hasVariants ? 'With Variants' : 'Simple'}
                                                            size="small" variant="outlined"
                                                            sx={{ fontSize: '0.65rem', height: 20 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {pp.status === 'uploading' ? (
                                                            <CircularProgress size={16} />
                                                        ) : (
                                                            <Chip
                                                                label={statusLabel(pp.status)}
                                                                size="small"
                                                                color={statusColor(pp.status) as any}
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.65rem', height: 20 }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ maxWidth: 360 }}>
                                                        {pp.error && (
                                                            <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                                                                ✗ {pp.error}
                                                            </Typography>
                                                        )}
                                                        {pp.warnings && pp.warnings.map((w, wi) => (
                                                            <Typography key={wi} variant="caption" color="warning.dark" sx={{ display: 'block' }}>
                                                                ⚠ {w}
                                                            </Typography>
                                                        ))}
                                                        {!pp.error && (!pp.warnings || pp.warnings.length === 0) && (
                                                            <Typography variant="caption" color="text.secondary">—</Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            {/* ── Footer ── */}
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
                {phase === 'idle' && (
                    <>
                        <Button onClick={handleClose} variant="outlined" size="small">Cancel</Button>
                        <Button
                            component="label"
                            variant="contained"
                            size="small"
                            startIcon={<UploadIcon />}
                            disabled={loadingRef}
                        >
                            Select CSV File
                            <input type="file" hidden accept=".csv" onChange={handleFileChange} />
                        </Button>
                    </>
                )}

                {phase === 'preview' && (
                    <>
                        <Button onClick={handleReset} variant="outlined" size="small" startIcon={<RefreshIcon />}>
                            Reset
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        {errorCount > 0 && (
                            <Typography variant="caption" color="error.main" sx={{ alignSelf: 'center' }}>
                                {errorCount} row{errorCount !== 1 ? 's' : ''} with errors will be skipped
                            </Typography>
                        )}
                        <Button
                            onClick={handleUpload}
                            variant="contained"
                            size="small"
                            startIcon={<UploadIcon />}
                            disabled={pendingCount === 0}
                        >
                            Upload {pendingCount} Product{pendingCount !== 1 ? 's' : ''}
                        </Button>
                    </>
                )}

                {phase === 'uploading' && (
                    <Button variant="outlined" size="small" disabled>
                        Uploading…
                    </Button>
                )}

                {phase === 'done' && (
                    <>
                        <Button onClick={handleReset} variant="outlined" size="small" startIcon={<RefreshIcon />}>
                            Upload Another
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button onClick={handleClose} variant="contained" size="small">
                            Close
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default BulkProductUpload;